import LOCALS from 'commons/locals';
import {
  findLabelByValue,
  PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
} from 'commons/options';
import CopyButton from 'components/copy-button';
import { ReactNode, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ProductUpdateInfoNewType } from 'types/pms';
import formatTime from 'utils/formatTime';
import { PUBLISH_STATUS_OPTION_LIST } from 'constant/pms-product';

const InfoList = ({
  items,
}: {
  items: {
    key: string;
    label: ReactNode;
    children: ReactNode;
  }[];
}) => {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-5 mb-12">
      {items.map((item) => {
        return (
          <div key={item.key}>
            <div className="text-sm font-medium text-gray-500">
              {item.label}
            </div>
            <div className="mt-1 text-sm font-medium whitespace-pre-line break-words">
              {item.children}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export type ProductBaseInfoProps = {
  productId?: ProductUpdateInfoNewType['id'];
  sourceProductId?: ProductUpdateInfoNewType['sourceProductId'];
  recycleOrderEmail?: NonNullable<
    ProductUpdateInfoNewType['recycleOrder']
  >['email'];
  recycleOrderMemberId?: NonNullable<
    ProductUpdateInfoNewType['recycleOrder']
  >['memberId'];
  recycleOrderId?: NonNullable<ProductUpdateInfoNewType['recycleOrder']>['id'];
  recycleOrderCode?: NonNullable<
    ProductUpdateInfoNewType['recycleOrder']
  >['code'];
  publishStatus?: ProductUpdateInfoNewType['publishStatus'];
  stockStatus?: ProductUpdateInfoNewType['stockStatus'];
  soldOrderId?: ProductUpdateInfoNewType['orderId'];
  soldTime?: ProductUpdateInfoNewType['soldTime'];
  entryTime?: ProductUpdateInfoNewType['entryTime'];
};

const ProductBaseInfo = ({
  productId,
  recycleOrderEmail,
  recycleOrderMemberId,
  recycleOrderId,
  recycleOrderCode,
  publishStatus,
  stockStatus,
  soldOrderId,
  soldTime,
  entryTime,
  sourceProductId,
}: ProductBaseInfoProps) => {
  const baseInfoList = useMemo(() => {
    const list: {
      key: string;
      label: string | React.ReactNode;
      children: React.ReactNode;
    }[] = [];

    if (productId) {
      list.push({
        key: 'product_id',
        label: <Trans i18nKey={LOCALS.sales_product_id} />,
        children: (
          <CopyButton copyText={productId.toString()}>{productId}</CopyButton>
        ),
      });
    }

    if (sourceProductId) {
      list.push({
        key: 'source_product_id',
        label: '源商品ID',
        children: (
          <Link to={`/pms/product-edit/${sourceProductId}`}>
            {sourceProductId}
          </Link>
        ),
      });
    }

    if (recycleOrderEmail && recycleOrderMemberId) {
      list.push({
        key: 'recycle_order_email',
        label: <Trans i18nKey={LOCALS.rzdMoKAcEc} />,
        children: (
          <Link to={`/ums/member-view/${recycleOrderMemberId}`}>
            {recycleOrderEmail}
          </Link>
        ),
      });
    }

    if (recycleOrderId) {
      list.push({
        key: 'recycle_order_id',
        label: <Trans i18nKey={LOCALS.ZQDTJFXciM} />,
        children: (
          <Link to={`/rrs/recycling-consignment-detail/${recycleOrderId}`}>
            {recycleOrderCode}
          </Link>
        ),
      });
    }

    list.push({
      key: 'publish_status',
      label: <Trans i18nKey={LOCALS.publish_status} />,
      children: (
        <span>
          {findLabelByValue(publishStatus, PUBLISH_STATUS_OPTION_LIST)}
        </span>
      ),
    });

    if (stockStatus) {
      list.push({
        key: 'stock_status',
        label: <Trans i18nKey={LOCALS.uMihwkqqts} />,
        children: (
          <span>
            {findLabelByValue(
              stockStatus,
              PMS_PRODUCT_STOCK_STATUS_OPTION_LIST,
            )}
          </span>
        ),
      });
    }

    if (entryTime) {
      list.push({
        key: 'entry_time',
        label: <Trans i18nKey={LOCALS.juusvCbRWo} />,
        children: formatTime(entryTime),
      });
    }

    if (soldOrderId) {
      list.push({
        key: 'sold_order_id',
        label: <Trans i18nKey={LOCALS.bxYXkMWIQe} />,
        children: (
          <Link to={`/oms/order-view/${soldOrderId}`}>{soldOrderId}</Link>
        ),
      });
    }

    if (soldTime) {
      list.push({
        key: 'sold_time',
        label: <Trans i18nKey={LOCALS.UNXRchiSDw} />,
        children: formatTime(soldTime),
      });
    }

    return list;
  }, [
    entryTime,
    productId,
    publishStatus,
    recycleOrderCode,
    recycleOrderEmail,
    recycleOrderId,
    recycleOrderMemberId,
    soldOrderId,
    soldTime,
    stockStatus,
    sourceProductId,
  ]);

  return productId ? <InfoList items={baseInfoList} /> : null;
};

export default ProductBaseInfo;
